import { db, type QueuedWrite } from './db';
import * as api from './api';

// Offline write queue. Every mutation is paired with an idempotency_key the
// moment the user commits the action — even if we're offline, the optimistic
// UI is final and the replay is safe. The server's write_log dedupes.

type Drainable = (w: QueuedWrite) => Promise<void>;

const handlers: Record<string, Drainable> = {
  '/pins': async (w) => { await api.upsertPin(w.body as api.UpsertPinInput); },
  '/memories': async (w) => { await api.attachMemory(w.body as api.AttachMemoryInput); },
};

export async function enqueue(write: Omit<QueuedWrite, 'created_at' | 'attempts'>): Promise<void> {
  await db.writes.put({ ...write, created_at: Date.now(), attempts: 0 });
}

export async function pendingCount(): Promise<number> {
  return db.writes.count();
}

let draining = false;

export async function drain(): Promise<{ ok: number; failed: number }> {
  if (draining) return { ok: 0, failed: 0 };
  draining = true;
  let ok = 0, failed = 0;
  try {
    const all = await db.writes.orderBy('created_at').toArray();
    for (const w of all) {
      const handler = handlers[w.endpoint];
      if (!handler) { await db.writes.delete(w.idempotency_key); continue; }
      try {
        await handler(w);
        await db.writes.delete(w.idempotency_key);
        ok++;
      } catch (err) {
        failed++;
        await db.writes.update(w.idempotency_key, {
          attempts: w.attempts + 1,
          last_error: (err as Error).message,
        });
        // Stop on the first failure — likely network. Try again next tick.
        break;
      }
    }
  } finally {
    draining = false;
  }
  return { ok, failed };
}

// Drain whenever connectivity returns or the tab regains focus. These are the
// two natural moments — we don't poll.
export function startAutoDrain(): () => void {
  const handle = () => { void drain(); };
  window.addEventListener('online', handle);
  window.addEventListener('focus', handle);
  void drain();
  return () => {
    window.removeEventListener('online', handle);
    window.removeEventListener('focus', handle);
  };
}
